/**
 * ONE-TRUTH placement + child-preservation math for EXPLODE of a NESTED INSERT
 * (a block reference baked into ANOTHER block's definition — DXF INSERT inside
 * the BLOCKS section).
 *
 * Both EXPLODE paths call THIS so the optimistic shell-preview mutation
 * (`packages/command-engine/src/blockMutations.ts` `explodeInsertMutation`,
 * → `EntityPreview`) and the reconciled persisted-document reducer
 * (`apps/web/src/app/state/blockReducerHelpers.ts` `explodeBlockInsertInDocument`,
 * → typed `DrawingEntity`) can NOT diverge (shell↔doc parity — the SAME rationale
 * that backs the sibling `ellipseExplodeGeometry.ts` / `attribExplodePlacement.ts`).
 *
 * # AutoCAD parity — single-level EXPLODE keeps a nested INSERT an INSERT
 *
 * Sources: Autodesk EXPLODE reference ("If the block reference contains nested
 * block references, they are NOT exploded — the result is the next lower level of
 * block references"); ezdxf `Insert.explode()` (a nested INSERT is re-emitted as an
 * INSERT with the outer transform applied, its content NOT recursed); the render
 * truth (`scene3d/adapters/typed/blockFromEntity.ts` Carve-out 2 nested-INSERT
 * recursion + `attdefFromEntity.ts` finding 5).
 *
 * EXPLODE replaces the parent INSERT with the block-definition children mapped
 * through the parent's placement affine (`matrix` = `T(origin)·R·S·T(−basePoint)`).
 * A nested `Block` child is re-emitted as a top-level INSERT that renders
 * IDENTICALLY to the sub-tree it replaced:
 *
 *   - **insertionPoint** rides the FULL parent affine (`matrix · childInsertion`)
 *     — the same map every other block child gets. (2D; the child's own z
 *     ELEVATION follows the pre-existing systemic 2D-explode limitation the
 *     non-ellipse children share, tracked separately — NOT re-introduced here.)
 *   - **scaleX / scaleY / scaleZ** COMPOSE with the parent per-axis scale as
 *     `guardScale(child.scale) · guardScale(parentScale)`. scaleZ was previously
 *     kept verbatim — a child-drop of the parent's Z scale (a nested INSERT
 *     under a `scaleZ 2` parent kept `scaleZ 1`, so a 3D sub-block lost half its
 *     Z extent on explode). Composing all three axes matches how the render
 *     accumulates a nested INSERT (`blockFromEntity.ts` Carve-out 2
 *     `t.scale.{x,y,z} · guardScale(child)`, where `t.scale` is itself
 *     `guardScale(parent)`). The GUARD on BOTH operands (@codex round-19 F3) is
 *     load-bearing, not cosmetic: the render normalises a degenerate (0 /
 *     non-finite) scale component to 1 at EVERY level BEFORE composing
 *     (`blockFromEntity.guardScale`). A raw `child.scaleZ · parentScaleZ` could
 *     emit `scaleZ 0` (parent `scaleZ 0` × child `scaleZ 2`), and the re-emitted
 *     top-level INSERT is guarded only ONCE — so `0` would normalise to `1`,
 *     rendering Z scale `1` after explode where it was `2` before. Guarding each
 *     operand first (`guardScale(0)·guardScale(2) = 1·2 = 2`) reproduces the
 *     render's per-level normalisation exactly; for the normal non-degenerate
 *     case `guardScale` is the identity, so no golden output shifts.
 *   - **rotation** COMPOSES (`child.rotation + parentRotation`) — the render's
 *     nested `t.rotationDeg + child.rotation`.
 *   - **columnCount / rowCount / columnSpacing / rowSpacing** collapse to a
 *     SINGLE CELL (`1 / 1 / 0 / 0`). A nested INSERT's MINSERT grid is NEVER
 *     expanded by the pre-explode RENDER: both the scene3d flatten
 *     (`blockFromEntity.ts` `flattenTypedInsert` Carve-out 2) and the PDF plot
 *     (`insertConverter.ts` `walkBlock` "Block" case) read ONLY
 *     insertion/scale/rotation for a nested `Block` child and never consult its
 *     count/spacing. The re-emit is bound by this module's shell↔doc-parity-
 *     WITH-RENDER contract (module header top): a TOP-LEVEL INSERT's MINSERT
 *     grid IS expanded by `flattenTypedInsert`, so preserving a non-1 count
 *     would make the exploded top-level INSERT render N cells where the
 *     pre-explode nested child rendered exactly ONE — EXPLODE would visibly
 *     MULTIPLY the geometry, which it must never do (@codex round-20 F3). The
 *     faithful re-emit is the single cell the render actually drew; a
 *     subsequent explode of the resulting plain INSERT dives one more level,
 *     still never grid-expanding a nested child.
 *
 *     KNOWN CROSS-PATH INCONSISTENCY (@codex round-22 P2). The BOUNDS/select/
 *     grips path (`cad-core/blockGeometry.ts` `childEntityBounds` →
 *     `extendForMinsertGrid`, Codex #467 P2) AND DXF export (INSERT array
 *     groups 70/71/44/45 are emitted for every INSERT) DO treat a nested
 *     INSERT's count/spacing as real — a nested MINSERT contributes a
 *     grid-sized AABB and round-trips its array fields, diverging from what the
 *     render actually draws (one cell). This slice deliberately follows the
 *     RENDER (the parity anchor for what the user sees at explode time), NOT the
 *     bounds/export view: making EXPLODE alone preserve the count while the
 *     render still collapses would desync the exploded document from its own
 *     render. Reconciling render↔bounds↔export on nested-MINSERT semantics
 *     (whether AutoCAD tiles a block-nested array — the DXF/ezdxf balance says
 *     it does, which would make the render+PDF the paths to fix, not this
 *     explode) is a dedicated cross-path PR, not this explode fix.
 *
 * The caller PRESERVES the remaining child-INSERT fields verbatim (extrusion OCS
 * normal, customProperties / visibilityState / representationOverrides dynamic
 * data, annotationScales / annotativeContexts / creationScaleName annotative
 * data) — dropping any of them is the "child-drop" defect this slice closes.
 *
 * # The nested INSERT's own ATTRIBs — world-vs-block-local (#470, Lane F)
 *
 * A nested INSERT may carry its OWN `attributes[]` (an INSERT-with-ATTRIBs baked
 * into a block — `blockFromEntity.ts` "a nested INSERT may itself carry ATTRIBs";
 * the export twin `dxfBridge.blocksSection.ts` round-4 FINDING 1). Per the #470
 * invariant a SET `overridePosition` is the RESOLVED world point in the frame the
 * ATTRIB is authored in — for a nested INSERT that is the ENCLOSING block's local
 * frame (`attdefFromEntity.ts` finding 5: its OUTER residual is exactly the parent
 * level's `t`). When the parent INSERT is exploded, that enclosing frame maps to
 * world via `matrix`, so the ATTRIB's `overridePosition` MUST ride `matrix` (the
 * SAME lift the child INSERT's `insertionPoint` gets), and its absolute
 * height/rotation bake in the parent scale/rotation the OUTER residual carried at
 * render (height · |parentScaleY|, rotation + parentRotation — the render's world
 * regime scalar conventions, `attdefFromEntity.ts renderAttributeValueLines` +
 * `blockTransform.ts` height·|scaleY| / rotation+rot). Otherwise the label would
 * TELEPORT to raw block-definition coordinates while its geometry moved to world
 * — the nested twin of the #470 double-transform / child-drop class.
 *
 * The DEFAULTS must be MATERIALISED, not carried as `undefined` (@codex round-19
 * F1). At render the nested ATTRIB's OUTER residual is the parent INSERT's full
 * transform, so the world regime resolves its size/angle from the DXF group
 * defaults `overrideHeight ?? 2.5` / `overrideRotation ?? 0` and THEN scales /
 * rotates them by the parent (`attdefFromEntity.resolvePlacement` world branch →
 * `renderAttributeValueLines` → `transformBlockChild` Text). After explode the
 * re-emitted ATTRIB rides an IDENTITY outer transform (a top-level single-cell
 * INSERT), so a still-`undefined` height/rotation would fall back to the UNscaled
 * `2.5` / `0` — a scaled or rotated parent would shrink / straighten the label on
 * explode. So an unset height is materialised to `2.5 · |parentScaleY|` and an
 * unset rotation to `0 + parentRotationFold`, exactly the values the render baked
 * in before. (For a parent `scale 1 / rotation 0` this is byte-identical to
 * leaving them unset — `2.5·1 = 2.5`, `0+0 = 0`.)
 *
 * The parent rotation added is the render's SCALAR-angle convention, NOT the raw
 * `parentRotationDeg` (@codex round-19 F2). `transformBlockChild` folds an extra
 * 180° into every scalar text rotation for a DOUBLE-NEGATIVE parent scale
 * (`scalarRotationDeg = scaleX<0 && scaleY<0 ? rot+180 : rot`, because
 * `S(−s,−s) ≡ R(180°)·S(s,s)` — a point reflection, det > 0, NOT a mirror). The
 * lift bakes the SAME fold so an explicitly positioned nested ATTRIB under a
 * double-negative parent keeps its pre-explode orientation instead of flipping
 * 180°. A SINGLE-negative (true mirror, det < 0) parent deliberately keeps the
 * raw sum — the legacy-pinned convention `transformBlockChild` also uses (glyph
 * reflection is the S10-XFORM residual, not this slice).
 *
 * An UNSET `overridePosition` is a block-local template placement that follows the
 * child block's OWN ATTDEF template through the (unchanged relative) child INSERT
 * transform at render — so it rides through EXPLODE VERBATIM (the re-emitted
 * top-level INSERT resolves the template exactly as it did nested).
 *
 * # KNOWN LIMITATION — ATTRIB glyph WIDTH FACTOR under a non-uniform parent
 *
 * The lift materialises the ATTRIB's absolute HEIGHT / ROTATION / STYLE because
 * those are ATTRIB-sourced fields the render's world regime consults directly
 * (`attdefFromEntity.resolvePlacement` reads `attr.overrideHeight/Rotation/Style`).
 * The rendered glyph WIDTH FACTOR is NOT: the world regime sources it from the
 * BLOCK-DEFINITION ATTDEF (`attdefFromEntity` builds the style with
 * `widthFactor: attdef.widthFactor`), and `blockInsertAttributeSchema` carries NO
 * per-ATTRIB width field. Pre-explode the parent OUTER transform multiplies that
 * ATTDEF width by `|parentScaleX| / |parentScaleY|` (`transformBlockChild` Text
 * case), so under a NON-uniform parent the label renders narrower/wider; after
 * explode the identity-outer re-emit renders the bare `attdef.widthFactor` (ratio
 * 1), a visible width shift whenever `scaleX !== scaleY` (@codex round-20 F1).
 *
 * This is the SAME non-uniform-scale text-metric residual class `blockTransform.ts`
 * already documents (the rotated-text-under-non-uniform shear routed to
 * S10-XFORM). A faithful fix needs a DEDICATED seam-complete PR, not this explode
 * slice: an ATTRIB `overrideWidth` channel added to the model WITH full DXF
 * import/export symmetry (group 41) AND consumed by the render's world regime,
 * PLUS a block-definition ATTDEF-width lookup here (the lift receives only the
 * ATTRIB — the width BASE lives on the ATTDEF it cannot currently reach). Under a
 * UNIFORM parent (`|scaleX| == |scaleY|`, the overwhelming common case) the ratio
 * is 1 and there is no discrepancy.
 */

import type {
  AnnotScaleContext,
  BlockChildEntity,
  BlockInsertAttribute,
  BlockInsertEntity,
} from "@dxf-ai-editor/drawing-model";

// The render's legacy scale guard (`blockFromEntity.guardScale`,
// `flattenBlockInsertEntity:1080-1084`): a degenerate (non-finite / ~zero) scale
// component normalises to 1. The render applies it to EVERY INSERT scale at
// EVERY level before composing, so EXPLODE must guard each operand the same way
// to reproduce a re-emitted INSERT that renders identically (module header
// "scaleX / scaleY / scaleZ" — @codex round-19 F3). Identity for a normal
// non-degenerate value, so golden outputs are untouched. ONE-TRUTH within this
// package: imported from the sibling `attribExplodePlacement.ts` (its
// `resolveMinsertCellOffset` guards the SAME render truth for the MINSERT
// grid-step math) instead of a second local copy.
import { guardScale } from "./attribExplodePlacement";
import type { ExplodeAffineMatrix } from "./ellipseExplodeGeometry";

/** The `Block` member of the block-child union (a nested INSERT). */
export type NestedInsertChild = Extract<BlockChildEntity, { type: "Block" }>;

export interface NestedInsertExplodeParams {
  /** The nested `Block` child from the parent block definition. */
  child: NestedInsertChild;
  /** THIS cell's FULL parent INSERT affine (`T(origin)·R·S·T(−basePoint)`). */
  matrix: ExplodeAffineMatrix;
  /** Parent INSERT rotation (degrees). */
  parentRotationDeg: number;
  /** Parent INSERT per-axis scale. */
  scaleX: number;
  scaleY: number;
  scaleZ: number;
}

/**
 * The placement fields common to BOTH explode paths (the reducer's typed
 * `DrawingEntity` and the shell's `SimpleBlockInsertEntity` both spread these).
 */
export interface NestedInsertPlacement {
  insertionPoint: { x: number; y: number };
  scaleX: number;
  scaleY: number;
  scaleZ: number;
  rotation: number;
  columnCount: number;
  rowCount: number;
  columnSpacing: number;
  rowSpacing: number;
}

/**
 * Compose the world placement of a nested INSERT re-emitted by EXPLODE (module
 * header "AutoCAD parity"). The caller wraps this with the entity-shape fields it
 * needs (id / spaceId / inherited base props / preserved data fields).
 */
export function composeNestedInsertPlacement(
  params: NestedInsertExplodeParams,
): NestedInsertPlacement {
  const { child, matrix, parentRotationDeg, scaleX, scaleY, scaleZ } = params;
  const p = child.insertionPoint;
  return {
    // Inlined `ExplodeAffineMatrix` apply (single use — the mapping documented on
    // its declaration: `worldX = a·x + c·y + e`, `worldY = b·x + d·y + f`), matching
    // the sibling `attribExplodePlacement.ts` precedent — not a reusable helper.
    insertionPoint: {
      x: matrix.a * p.x + matrix.c * p.y + matrix.e,
      y: matrix.b * p.x + matrix.d * p.y + matrix.f,
    },
    // Guard EACH operand before composing (module header "scaleX / scaleY /
    // scaleZ" — @codex round-19 F3): the render normalises a degenerate scale to
    // 1 at every level, so `guardScale(parent 0)·guardScale(child 2) = 2`, not a
    // raw `0·2 = 0` that the single re-emit guard would then flip to 1.
    scaleX: guardScale(child.scaleX) * guardScale(scaleX),
    scaleY: guardScale(child.scaleY) * guardScale(scaleY),
    scaleZ: guardScale(child.scaleZ) * guardScale(scaleZ),
    rotation: child.rotation + parentRotationDeg,
    // SINGLE CELL (module header "columnCount / rowCount …") — a nested
    // INSERT's MINSERT grid is NEVER expanded by the pre-explode render (the
    // scene3d flatten's Carve-out 2 + the PDF `walkBlock` "Block" case both
    // ignore a nested child's count/spacing), so the re-emitted TOP-LEVEL
    // INSERT must stay a plain single-cell INSERT. Promoting a non-1 count
    // would render/export repeated copies never drawn before the explode
    // (@codex round-20 F3).
    columnCount: 1,
    rowCount: 1,
    columnSpacing: 0,
    rowSpacing: 0,
  };
}

export interface NestedAttribLiftContext {
  /** THIS cell's FULL parent INSERT affine (same `matrix` as the placement). */
  matrix: ExplodeAffineMatrix;
  /** Parent INSERT x-scale — needed for the render's double-negative rotation
   *  fold (`scaleX<0 && scaleY<0 → +180`, @codex round-19 F2). */
  scaleX: number;
  /** Parent INSERT y-scale — the render's world-regime height axis (|scaleY|). */
  scaleY: number;
  /** Parent INSERT rotation (degrees) — added to a world-regime rotation. */
  parentRotationDeg: number;
}

/** The DXF group defaults the render's world-regime resolves for an unset
 *  ATTRIB height (group 40) / rotation (group 50) —
 *  `attdefFromEntity.resolvePlacement` (`overrideHeight ?? 2.5`,
 *  `overrideRotation ?? 0`). Materialised on lift so the re-emitted top-level
 *  ATTRIB (identity outer transform) still carries the parent scale/rotation the
 *  render baked into these defaults nested (@codex round-19 F1). */
const DEFAULT_ATTRIB_HEIGHT = 2.5;
const DEFAULT_ATTRIB_ROTATION = 0;

/**
 * Lift a nested INSERT's OWN ATTRIB from the enclosing-block frame to world when
 * its owning parent INSERT is exploded (module header "world-vs-block-local").
 *
 *   - SET `overridePosition` (WORLD/absolute in the enclosing frame) → ride the
 *     parent `matrix`, and bake the parent scale/rotation the render's OUTER
 *     residual carried into the absolute height/rotation. Height is ALWAYS
 *     materialised to `(overrideHeight ?? 2.5) · |parentScaleY|` and rotation to
 *     `(overrideRotation ?? 0) + parentRotationFold` — the DXF-group defaults the
 *     render's world regime resolves for an unset field, baked with the parent
 *     transform so an identity-outer re-emit reproduces them (@codex round-19
 *     F1). The fold is the render's SCALAR convention: `+180` for a
 *     DOUBLE-negative parent scale (`S(−s,−s) ≡ R(180°)`, @codex round-19 F2),
 *     the raw sum otherwise. Style / tag / value / invisible / rawGroups pass
 *     through verbatim.
 *   - UNSET → verbatim placement (block-local template, rides the child
 *     INSERT's own transform at render — unchanged by the parent explode).
 *
 * In BOTH branches the ATTRIB `handle` (DXF group 5) is CLEARED. EXPLODE
 * re-emits this nested ATTRIB as a BRAND-NEW top-level ATTRIB while the source
 * nested INSERT stays inside the still-exported block definition; an imported
 * ATTRIB carries a real handle, so a verbatim copy makes BOTH the block-def
 * ATTRIB and the exploded ATTRIB save the SAME handle (duplicate entity
 * handle). A blank handle makes the export mint a fresh unique one
 * (`dxfBridge.attribExportPosition.drawingAttribToDxfAttrib`:
 * `if (attr.handle) common.handle = attr.handle` — blank → conformant-skeleton
 * allocator). Tag / value / placement / height / rotation / style / visibility
 * are all preserved (@codex round-21 F2).
 */
export function liftNestedInsertAttribute(
  attr: BlockInsertAttribute,
  ctx: NestedAttribLiftContext,
): BlockInsertAttribute {
  // Re-mint: clear the copied handle so a re-emit of an imported ATTRIB does
  // not collide with the still-block-def-resident source ATTRIB on save.
  const source = attr.handle ? { ...attr, handle: "" } : attr;
  if (source.overridePosition === undefined) {
    return source;
  }
  const { matrix, parentRotationDeg } = ctx;
  // Guard the parent scales the way the render does before deriving the
  // world-regime |scaleY| height axis + the double-negative rotation fold — a
  // degenerate parent scale normalises to 1 (positive), so |scaleY| = 1 and the
  // fold does not fire, exactly matching `blockFromEntity.guardScale`.
  const scaleX = guardScale(ctx.scaleX);
  const scaleY = guardScale(ctx.scaleY);
  const p = source.overridePosition;
  // Render's `transformBlockChild` scalar-rotation convention: a DOUBLE-negative
  // scale is a point reflection (det > 0) that folds an extra 180° into the
  // angle sum; a single-negative mirror keeps the raw sum (@codex round-19 F2).
  const rotationFold =
    scaleX < 0 && scaleY < 0 ? parentRotationDeg + 180 : parentRotationDeg;
  return {
    ...source,
    // Inlined `ExplodeAffineMatrix` apply (single use — see the note in
    // `composeNestedInsertPlacement`; the SAME lift the child INSERT's
    // insertionPoint gets, so the label stays glued to it).
    overridePosition: {
      x: matrix.a * p.x + matrix.c * p.y + matrix.e,
      y: matrix.b * p.x + matrix.d * p.y + matrix.f,
    },
    // ALWAYS materialise (@codex round-19 F1): the render defaults an unset
    // height/rotation to 2.5 / 0 and then the parent OUTER transform scales /
    // rotates them; an identity-outer re-emit must bake those in or the label
    // shrinks / straightens on explode. Byte-identical to leaving them unset for
    // a parent `scale 1 / rotation 0` (`2.5·1`, `0+0`).
    overrideHeight:
      (source.overrideHeight ?? DEFAULT_ATTRIB_HEIGHT) * Math.abs(scaleY),
    overrideRotation:
      (source.overrideRotation ?? DEFAULT_ATTRIB_ROTATION) + rotationFold,
  };
}

/**
 * Lift a nested INSERT's OWN annotative per-scale `blockref` context from the
 * enclosing-block frame to WORLD when its owning parent INSERT is exploded
 * (module header "world-vs-block-local", annotative twin of the ATTRIB lift).
 *
 * A `blockref` context (`AcDbBlkRefObjectContextData`, INSERT variant) carries
 * the reference's per-CANNOSCALE insertion / scaleX / scaleY / rotation. The
 * render (`renderBlockInstance`) applies a matched context as ABSOLUTE
 * top-level overrides — it does NOT re-run the INSERT affine — so a verbatim
 * copy would TELEPORT the re-emitted top-level INSERT to the RAW nested
 * coordinates the moment that scale becomes active. Compose the context
 * placement through the SAME parent affine / scale / rotation as
 * `composeNestedInsertPlacement` (same `matrix` apply, same guarded scale
 * product, same rotation sum) so the context stays glued to the lifted base
 * placement (@codex round-21 F1).
 *
 * The stale `ownerHandle` (DXF group 330 — the ORIGINAL nested INSERT's
 * extension dict) is CLEARED: the context now belongs to a NEW top-level
 * INSERT whose ext-dict the annotative export mints fresh
 * (`dxfBridge.annotativeExport` anchors on
 * `contexts.find((c) => c.ownerHandle)` and re-mints when absent), so keeping
 * the old handle would stamp a stale/foreign FK onto the exploded INSERT.
 *
 * Non-`blockref` variants cannot legitimately appear on a Block entity (an
 * INSERT resolves to the BlkRef context class), but ride through verbatim for
 * total data preservation should a malformed source carry one.
 */
export function liftNestedInsertBlockrefContext(
  context: AnnotScaleContext,
  ctx: NestedAttribLiftContext,
): AnnotScaleContext {
  if (context.kind !== "blockref") return context;
  const { matrix, parentRotationDeg } = ctx;
  const p = context.insertion;
  // Drop the stale ORIGINAL-INSERT ext-dict FK (see doc) — export re-mints.
  const { ownerHandle: _staleOwnerHandle, ...rest } = context;
  return {
    ...rest,
    insertion: {
      x: matrix.a * p.x + matrix.c * p.y + matrix.e,
      y: matrix.b * p.x + matrix.d * p.y + matrix.f,
    },
    // Guard EACH operand before composing — identical to
    // `composeNestedInsertPlacement.scaleX/scaleY` (module header, round-19 F3).
    scaleX: guardScale(context.scaleX) * guardScale(ctx.scaleX),
    scaleY: guardScale(context.scaleY) * guardScale(ctx.scaleY),
    rotation: context.rotation + parentRotationDeg,
  };
}

/**
 * Strip the round-trip-only per-INSERT representation LINKAGE handles from a
 * nested INSERT's `representationOverrides` when EXPLODE re-emits it as a NEW
 * top-level INSERT.
 *
 * `representationDataHandle` (group 5 of the ACDB_BLOCKREPRESENTATION_DATA
 * record, mirrored into the INSERT's `common.extensionDictionaryHandle` by
 * `dxfBridge.entityConversion`) and `representationEtag` (AcDbBlockRepETag
 * XData group 1071) both point a block reference at ONE per-INSERT
 * representation object. The source nested INSERT stays inside the
 * still-exported block definition, so copying these verbatim makes BOTH
 * references save the SAME linkage handle — two INSERTs pointing at one
 * representation record. The MEANINGFUL divergence (`parameterOverrides` +
 * `currentVisibilityStateId`) is preserved in the typed model (@codex
 * round-21 F3).
 *
 * KNOWN LIMITATION — the exploded INSERT's INDEPENDENT representation is not
 * yet re-serialised to DXF (@codex round-22 P2). `representationDataHandle`
 * points at a RAW-preserved OBJECTS-section entry (classed `'object'` in
 * `dxfBridge.conformantSkeleton.handles`; `referenceAudit.entityRefs`: "raw
 * OBJECTS handle, unverifiable — intentionally skipped"), and export only
 * mirrors the 360 XDICTIONARY linkage / 1071 etag WHEN those handle fields are
 * present — it does NOT yet MATERIALISE an `ACDB_BLOCKREPRESENTATION_DATA`
 * object from `parameterOverrides` (the deferred Phase N.4 export-side
 * follow-up — `dxfBridge.entityConversion` Phase N.4 comment: the dictionary
 * chain is "materialised by the OBJECTS-section writer … not in this commit").
 * So the choices here are (a) copy the handle → TWO block references owning ONE
 * ext-dict object (invalid DXF — an extension dictionary has a single 330
 * owner), (b) mint a fresh handle → a DANGLING 360 with no object written, or
 * (c) strip the linkage and keep `parameterOverrides` in the typed model. (c)
 * is the only non-corrupting option: the exploded INSERT's per-instance
 * representation survives editing / undo / re-inspection, and is emitted
 * faithfully once Phase N.4 lands the export-side object materialisation.
 * Faithful DXF re-serialisation of the exploded INSERT's own representation
 * needs that dedicated PR, NOT this explode slice (the same "documented
 * incomplete subsystem" carve-out as the ATTRIB width-factor limitation above).
 */
export function stripExplodedInsertRepresentationHandles(
  overrides: NonNullable<BlockInsertEntity["representationOverrides"]>,
): NonNullable<BlockInsertEntity["representationOverrides"]> {
  const stripped: NonNullable<BlockInsertEntity["representationOverrides"]> = {
    parameterOverrides: overrides.parameterOverrides,
  };
  if (overrides.currentVisibilityStateId !== undefined) {
    stripped.currentVisibilityStateId = overrides.currentVisibilityStateId;
  }
  return stripped;
}
